"use client";

import type { RowData } from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "#ui/table";

import {
  type DataTableInstance,
  defaultPageSizes,
  getDataTableFooterVisibility,
  useDataTable,
  useDataTableContext,
  type UseDataTableParams,
} from "./data-table.utils";
import { cx } from "./style.utils";
import {
  TableCellNotFound,
  TablePaginationButtonFirst,
  TablePaginationButtonLast,
  TablePaginationButtonNext,
  TablePaginationButtonPrevious,
  TablePaginationPageStatus,
  TablePaginationRowSelectionStatus,
  TablePaginationSelectPageSize,
} from "./table.components";

// -----------------------------------------------------------------------------

type RenderProp<TData extends RowData> = (
  table: DataTableInstance<TData>,
) => React.ReactNode;

export interface DataTableProps<TData extends RowData>
  extends
    UseDataTableParams<TData>,
    Omit<React.ComponentPropsWithoutRef<"div">, "children"> {
  /**
   * Footer slot. Defaults to `DataTableFooter` (row-selection status +
   * pagination, shown only when there is something to show). Pass `null` to
   * render no footer.
   */
  renderAfter?: RenderProp<TData> | null | undefined;
  renderBefore?: RenderProp<TData> | null | undefined;
  /** Content of the single "not found" row rendered when there are no rows. */
  renderNotFound?: RenderProp<TData> | undefined;
  /** Action rendered next to the selection status in the default footer. */
  renderRowSelectionButton?: RenderProp<TData> | undefined;
}

/**
 * Card-ready table: header/body from column defs, optional footer, and the
 * bottom-edge styling that depends on whether that footer is present.
 */
export function DataTable<TData extends RowData>({
  className,
  columns,
  data,
  options,
  renderAfter,
  renderBefore,
  renderNotFound,
  renderRowSelectionButton,
  ...props
}: DataTableProps<TData>) {
  const table = useDataTable({ columns, data, options });

  const { isPaginationVisible, isRowSelectionVisible } =
    getDataTableFooterVisibility(table);
  const hasFooter =
    renderAfter === null
      ? false
      : renderAfter
        ? true
        : isPaginationVisible || isRowSelectionVisible;

  return (
    <table.AppTable>
      <div
        {...props}
        className={cx(
          "@container",
          renderBefore
            ? "first:*:border-b first:*:border-shade-15 first:*:px-30px first:*:py-20px"
            : undefined,
          hasFooter
            ? "last:*:border-t last:*:border-shade-15 last:*:px-30px last:*:py-20px"
            : // Clip the last row's hover background to the parent Card's radius.
              "overflow-clip rounded-b-lg md:rounded-b-xl",
          className,
        )}
      >
        {renderBefore ? renderBefore(table) : null}
        <DataTableHeadless
          renderNotFound={renderNotFound ? () => renderNotFound(table) : null}
        />
        {renderAfter === null ? null : renderAfter ? (
          renderAfter(table)
        ) : hasFooter ? (
          <DataTableFooter>
            {renderRowSelectionButton
              ? () => renderRowSelectionButton(table)
              : null}
          </DataTableFooter>
        ) : null}
      </div>
    </table.AppTable>
  );
}

// -----------------------------------------------------------------------------

export interface DataTableHeadlessProps extends Omit<
  React.ComponentProps<"table">,
  "children"
> {
  renderNotFound?: (() => React.ReactNode) | null | undefined;
}

/** `<table>` markup only. Must render inside a `DataTable`. */
export function DataTableHeadless({
  renderNotFound,
  ...props
}: DataTableHeadlessProps) {
  const table = useDataTableContext();
  const rows = table.getRowModel().rows;

  return (
    <Table {...props}>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} data-table-head-id={header.id}>
                {header.isPlaceholder ? null : (
                  <table.FlexRender header={header} />
                )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {rows.length > 0 ? (
          rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} data-table-cell-id={cell.id}>
                  <table.FlexRender cell={cell} />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="[tbody>&:not([data-state=selected])]:hover:bg-transparent">
            <TableCellNotFound colSpan={table.getVisibleFlatColumns().length}>
              {renderNotFound ? renderNotFound() : undefined}
            </TableCellNotFound>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

// -----------------------------------------------------------------------------

export interface DataTableFooterProps extends Omit<
  React.ComponentPropsWithoutRef<"div">,
  "children"
> {
  /** Action rendered next to the selection status. */
  children?: (() => React.ReactNode) | null | undefined;
}

/**
 * Default footer: row-selection status (when selection is enabled) and
 * pagination (when there is more than one page). Must render inside a
 * `DataTable`.
 */
export function DataTableFooter({
  children,
  className,
  ...props
}: DataTableFooterProps) {
  const table = useDataTableContext();
  const { isPaginationVisible, isRowSelectionVisible } =
    getDataTableFooterVisibility(table);

  return (
    <div
      {...props}
      className={cx(
        "flex flex-col items-center gap-20px @xl:flex-row @xl:flex-wrap @xl:justify-between",
        isRowSelectionVisible || isPaginationVisible ? undefined : "hidden",
        !isRowSelectionVisible && isPaginationVisible
          ? "@xl:justify-end"
          : undefined,
        className,
      )}
    >
      {isRowSelectionVisible ? (
        <DataTableRowSelection>{children}</DataTableRowSelection>
      ) : null}
      {isPaginationVisible ? <DataTablePagination /> : null}
    </div>
  );
}

// -----------------------------------------------------------------------------

export interface DataTableRowSelectionProps extends Omit<
  React.ComponentPropsWithoutRef<"div">,
  "children"
> {
  /** Action rendered next to the selection status. */
  children?: (() => React.ReactNode) | null | undefined;
}

/** "n of m selected" status plus an optional action. Must render inside a `DataTable`. */
export function DataTableRowSelection({
  children,
  className,
  ...props
}: DataTableRowSelectionProps) {
  const table = useDataTableContext();

  return (
    <div
      {...props}
      className={cx(
        "flex w-full flex-col items-center gap-20px @xl:w-auto @xl:flex-row @xl:items-center",
        className,
      )}
    >
      {children ? <span className="self-stretch">{children()}</span> : null}
      <TablePaginationRowSelectionStatus
        selectedCount={table.getSelectedRowModel().rows.length}
        selectableCount={table.getCoreRowModel().rows.length}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------

export type DataTablePaginationProps = Omit<
  React.ComponentPropsWithoutRef<"div">,
  "children"
>;

/** Page-size select, page status and first/prev/next/last. Must render inside a `DataTable`. */
export function DataTablePagination({
  className,
  ...props
}: DataTablePaginationProps) {
  const table = useDataTableContext();
  const { pageIndex, pageSize } = table.state.pagination;

  return (
    <div
      {...props}
      className={cx(
        "flex w-full flex-col items-center gap-20px @xl:flex-row @xl:justify-between @[870px]:w-auto",
        className,
      )}
    >
      <div className="flex items-center gap-20px">
        <TablePaginationSelectPageSize
          pageSize={pageSize}
          pageSizes={defaultPageSizes}
          onChangePageSize={table.setPageSize}
        />
        <TablePaginationPageStatus
          pageCount={table.getPageCount()}
          pageIndex={pageIndex}
          onChangePageIndex={table.setPageIndex}
        />
      </div>
      <div className="flex items-center gap-10px">
        <TablePaginationButtonFirst
          onClick={() => table.firstPage()}
          disabled={!table.getCanPreviousPage()}
        />
        <TablePaginationButtonPrevious
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        />
        <TablePaginationButtonNext
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        />
        <TablePaginationButtonLast
          onClick={() => table.lastPage()}
          disabled={!table.getCanNextPage()}
        />
      </div>
    </div>
  );
}
